/**
 * HttpLogger is a utility class for logging events persistently and synchronizing them
 * with a remote endpoint in batches. It ensures logs are sent in chronological order,
 * supports retries with exponential backoff, and resumes pending log synchronization
 * after application restarts.
 *
 * Usage:
 * const logger = HttpLogger.create(config);
 * logger.log(logDetails);
 *
 */
import { Log } from '../../../types';
import { ApiV2Response } from '../../../types/utils';
import { nzApiClient } from '../../../utils';
import { defaultHttpLoggerConfig } from '../../constants';
import { HttpLoggerConfig } from '../../types';

export class HttpLogger {
  private config: Required<HttpLoggerConfig>;
  private isFlushing = false;
  private retryCount = 0;
  private isFlushScheduled = false;

  private constructor(config: Required<HttpLoggerConfig>) {
    this.config = config;
    this.setupUnloadListener();
  }

  static create(config: HttpLoggerConfig): HttpLogger {
    const finalConfig = {
      ...defaultHttpLoggerConfig,
      ...config,
    } as Required<HttpLoggerConfig>;

    if (!finalConfig.db) {
      throw new Error('Invalid Dixie DB');
    }

    if (!finalConfig.project) {
      throw new Error('Project name is required');
    }

    return new HttpLogger(finalConfig);
  }

  async save(log: Log): Promise<void> {
    const serialized = JSON.parse(JSON.stringify(log));
    await this.config.db.logs.add(serialized);
    this.scheduleFlush();
  }

  private scheduleFlush(delay = this.config.flushInterval * 1000): void {
    if (this.isFlushScheduled || this.isFlushing) return;

    this.isFlushScheduled = true;

    setTimeout(() => {
      this.isFlushScheduled = false;
      this.flush();
    }, delay);
  }

  private async flush(): Promise<void> {
    if (this.isFlushing) return;
    this.isFlushing = true;
    let success = false;

    try {
      await this.processLogsBatch(async batch => {
        await this.sendLogs(batch);
        await this.deleteLogs(batch);
      });
      this.retryCount = 0;
      success = true;
    } catch (error) {
      this.logError('flush', error);
    }

    // Reset flush flag BEFORE scheduling any new flush
    this.isFlushing = false;
    const hasPendingLogs = await this.hasPendingLogs();

    if (!success && hasPendingLogs) {
      this.handleRetry();
    } else if (success && hasPendingLogs) {
      this.scheduleFlush();
    }
  }

  private async processLogsBatch(
    processBatch: (batch: Log[]) => Promise<void>,
  ): Promise<void> {
    const logs = await this.getLogsBatch();
    const batches = this.splitLogsByPayloadSize(logs);

    for (const batch of batches) {
      await processBatch(batch);
    }
  }

  private async getLogsBatch(
    limit: number = this.config.batchSize,
  ): Promise<Log[]> {
    return this.config.db.logs.orderBy('time').limit(limit).toArray();
  }

  private splitLogsByPayloadSize(logs: Log[]): Log[][] {
    let currentBatch: Log[] = [];
    let currentBatchSize = 0;
    const batches: Log[][] = [];

    logs.forEach(log => {
      const logSize = JSON.stringify(log).length / 1024; // Size in KB
      if (currentBatchSize + logSize > this.config.maxPayloadSize) {
        batches.push(currentBatch);
        currentBatch = [];
        currentBatchSize = 0;
      }

      currentBatch.push(log);
      currentBatchSize += logSize;
    });

    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  }

  private async sendLogs(logs: Log[]): Promise<void> {
    await nzApiClient.rest<ApiV2Response<void>>({
      path: this.config.endpoint,
      method: 'POST',
      body: this.buildPayload(logs, false),
    });
  }

  private buildPayload(logs: Log[], stringify: true): string;
  private buildPayload(
    logs: Log[],
    stringify?: false,
  ): { project: string; logs: Log[] };

  private buildPayload(logs: Log[], stringify?: boolean) {
    const payload = { project: this.config.project, logs };
    return stringify ? JSON.stringify(payload) : payload;
  }

  private async deleteLogs(logs: Log[]): Promise<void> {
    const ids = logs.map(log => log.id);
    await this.config.db.logs.bulkDelete(ids);
  }

  private logError(context: string, error: any): void {
    console.error(`[${context}]`, error);
  }

  private handleRetry(): void {
    if (this.retryCount >= this.config.maxRetries) return;

    this.retryCount++;
    const backoffTime =
      this.config.flushInterval *
      Math.pow(this.config.retryBackoffFactor, this.retryCount) *
      1000; // Convert to milliseconds

    this.scheduleFlush(backoffTime);
  }

  private async hasPendingLogs(): Promise<boolean> {
    const count = await this.config.db.logs.count();
    return count > 0;
  }

  private setupUnloadListener(): void {
    window.addEventListener('beforeunload', async () => {
      if (this.isFlushing || !(await this.hasPendingLogs())) return;

      await this.processLogsBatch(async batch => {
        const payload = this.buildPayload(batch, true);

        if (!navigator.sendBeacon(this.config.endpoint, payload)) {
          console.warn(
            'Failed to send logs via sendBeacon; logs will remain in the database.',
          );
        } else {
          await this.deleteLogs(batch);
        }
      });
    });
  }
}
